import { type ReactNode, useEffect, useRef, useState } from "react"

import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

/**
 * A number that reads as a value and edits in place: shows the formatted
 * display, click (or focus + Enter) swaps in an input seeded with the raw
 * wire value, Enter/blur commits, Escape reverts. The commit handler owns
 * the round-trip (router.patch with preserveScroll); the authoritative
 * value always comes back from the server on re-render — this component
 * never keeps its own copy after commit.
 */
export function EditableCell({
  value,
  display,
  onCommit,
  testid,
  suffix,
  disabled = false,
  className,
  inputClassName,
}: {
  /** Raw editable value from the wire ("5.55"), exact — not the display. */
  value: string
  /** Formatted display ("$5.55"), or a rendered element (the GM chip). */
  display: ReactNode
  onCommit: (raw: string) => void
  testid: string
  /** Rendered after the value, outside the edit swap ("/SF", "%"). */
  suffix?: string
  disabled?: boolean
  className?: string
  inputClassName?: string
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editing) inputRef.current?.select()
  }, [editing])

  const open = () => {
    if (disabled) return
    setDraft(value)
    setEditing(true)
  }

  const commit = () => {
    setEditing(false)
    const trimmed = draft.trim()
    if (trimmed !== "" && trimmed !== value) onCommit(trimmed)
  }

  if (editing) {
    return (
      <span className={cn("inline-flex items-baseline gap-1", className)}>
        <Input
          ref={inputRef}
          autoFocus
          type="number"
          step="any"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit()
            if (e.key === "Escape") setEditing(false)
          }}
          className={cn("h-8 w-24 text-right tabular-nums", inputClassName)}
          data-testid={`${testid}-input`}
        />
        {suffix && <span className="text-sm text-ink-muted">{suffix}</span>}
      </span>
    )
  }

  return (
    <button
      type="button"
      onClick={open}
      disabled={disabled}
      className={cn(
        "inline-flex items-baseline gap-1 rounded px-1 tabular-nums",
        disabled
          ? "cursor-default"
          : "hover:bg-accent-faded focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent",
        className
      )}
      data-testid={testid}
    >
      <span>{display}</span>
      {suffix && <span className="text-sm font-normal text-ink-muted">{suffix}</span>}
    </button>
  )
}
